import React, { Dispatch, SetStateAction } from 'react';
import { isValid, isWithinInterval, parseISO } from 'date-fns';

import { UseOutletContextPurchase } from '../Purchase';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { RequirementContent } from '../../../../models/requirement.content.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Expense } from '../../../../models/expense.model';
import { accountingAccountSelected } from '../../../../scripts/list.selected.script';
import { formatContact, formatMoney } from '../../../../scripts/format.value.script';
import { accountingAccountCoefficientList } from '../../../../scripts/list.filter.script';
import { accountingAccountId } from '../../../../scripts/item.account.script';
import { taxPurchaseNonDeductibleCodes, taxPurchaseRecoverableCodes, taxPurchaseReverseChargeCodes } from '../../../../libraries/tax.code.library';

import AppInputAccountingAccount from '../../../../components/element/InputAccountingAccount';
import AppInputAccountingAccountTax from '../../../../components/element/InputAccountingAccountTax';
import { Company } from '../../../../models/company.model';

export interface AppPurchaseFormEntriesProps {
  component?: string,
  purchase: 'invoice' | 'expense',
  record: any | ProviderInvoice | Expense,
  contents: {value: {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[], valid: boolean[]},
  amounts: {value: {base: number, tax_rc: number, tax: number, rebate: number, content: number, debit: number, credit: number}, valid: boolean},
  contentTaxAccounts: {value: {content_id: string, accounting_account_tax: string, object: any}[], valid: boolean[]},
  setContents: Dispatch<SetStateAction<{value: {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[], valid: boolean[]}>>,
  setAmounts: Dispatch<SetStateAction<{value: {base: number, tax_rc: number, tax: number, rebate: number, content: number, debit: number, credit: number}, valid: boolean}>>,
  setContentTaxAccounts: Dispatch<SetStateAction<{value: {content_id: string, accounting_account_tax: string, object: any}[], valid: boolean[]}>>,
  companyForUserProp?: Company | null,
  settingsForCompanyProp?: any | null,
  accountingAccountsForCompanyProp?: any[] | null,
  accountingTaxAccountsForCompanyProp?: any[] | null,
  accountingExpenseAccountsForCompanyProp?: any[] | null,
  hideTaxAccounts?: boolean,
  groupHeaders?: {index: number, label: string}[],
  creditParty?: { label: string; name: string; accountCode: string },
};

const AppPurchaseFormEntries: React.FunctionComponent<AppPurchaseFormEntriesProps> = ({
  component, purchase, record, contents, amounts, contentTaxAccounts, setContents, setAmounts, setContentTaxAccounts,
  companyForUserProp, settingsForCompanyProp, accountingAccountsForCompanyProp, accountingTaxAccountsForCompanyProp, accountingExpenseAccountsForCompanyProp,
  hideTaxAccounts, groupHeaders, creditParty
}) => {
  let ctx: any = {}
  try { ctx = UseOutletContextPurchase() ?? {} } catch { ctx = {} }

  const companyForUser = companyForUserProp ?? ctx.companyForUser
  const settingsForCompany = settingsForCompanyProp ?? ctx.settingsForCompany
  const accountingAccountsForCompany = accountingAccountsForCompanyProp ?? ctx.accountingAccountsForCompany
  const accountingTaxAccountsForCompany = accountingTaxAccountsForCompanyProp ?? ctx.accountingTaxAccountsForCompany
  const accountingExpenseAccountsForCompany = accountingExpenseAccountsForCompanyProp ?? ctx.accountingExpenseAccountsForCompany
  const treasuryAccountsForCompany = ctx.treasuryAccountsForCompany

  const {lang} = StateLanguage()

  const currencyCode = record.currency?.code ?? companyForUser?.currency?.code ?? ''

  const displayRate = record.foreign_currency?.exchange_rate ?? null;
  const toDisplay = (v: number): number => {
    if (!displayRate || displayRate === 0) return v;
    return Math.round((v / displayRate) * 100) / 100;
  };

  function resolveTaxRate(taxCode: string): number {
    const fees = settingsForCompany?.tax_fees ?? []
    const docDate = record?.created_date
    const match = fees.find((f: any) => {
      if (f.code !== taxCode) return false
      if (!docDate || !f.effectiveFrom || !f.effectiveTo) return true
      try {
        return isValid(parseISO(docDate)) && isWithinInterval(parseISO(docDate), { start: parseISO(f.effectiveFrom), end: parseISO(f.effectiveTo) })
      } catch { return true }
    })
    return match?.rate ?? 0
  }

  function accumulatedEffectiveTaxDocuments(companyForUser: Company, content_accounts: {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[]): {deductible: number, non_deductible: number, reverse_charge: number} {
    let amountDeductible: number = 0
    let amountNonDeductible: number = 0
    let amountReverseCharge: number = 0

    for (const content_account of content_accounts) {
      if ((settingsForCompany?.subject_tax === true) && (settingsForCompany?.tax_method ?? 'effective') === 'effective') {
        if (taxPurchaseRecoverableCodes().includes(content_account.content.tax_code)) {
          amountDeductible += (content_account.content.tax_value ?? content_account.content.tax_amount ?? 0)
        }
        if (taxPurchaseReverseChargeCodes().includes(content_account.content.tax_code)) {
          const realRate = resolveTaxRate(content_account.content.tax_code)
          amountReverseCharge += realRate * content_account.content.price_net
        }
      }
      if ((settingsForCompany?.subject_tax === true) && (settingsForCompany?.tax_method ?? 'effective') === 'effective' && (settingsForCompany?.tax_declaration ?? 'invoicing') === 'invoicing') {
        if (taxPurchaseNonDeductibleCodes().includes(content_account.content.tax_code)) {
          amountNonDeductible += (content_account.content.tax_value ?? content_account.content.tax_amount ?? 0) * 0
        }
      }
    }

    return {deductible: amountDeductible, non_deductible: amountNonDeductible, reverse_charge: amountReverseCharge}
  }

  const allowedDebitAccount = (code: string): boolean => {
    const c = String(code);
    if (c.startsWith('3')) return false;
    if (c.startsWith('285')) return true;
    if (c.startsWith('100') || c.startsWith('101') || c.startsWith('102')) return false;
    if (c.startsWith('1') || c.startsWith('4') || c.startsWith('5') || c.startsWith('6') || c.startsWith('7') || c.startsWith('8')) return true;
    return false;
  };

  const buildDebitAccountList = (allAccounts: any[] | null): any[] => {
    if (!allAccounts) return [];
    return allAccounts.filter((a: any) => a.type === 'leave' && allowedDebitAccount(a.code));
  };

  const buildCreditNegativeAccountList = (allAccounts: any[] | null): any[] => {
    if (!allAccounts) return [];
    return allAccounts.filter((a: any) => a.type === 'leave' && allowedDebitAccount(a.code));
  };

  return (
    <div className="dataTables_wrapper">
      { companyForUser && accountingAccountsForCompany && accountingTaxAccountsForCompany && accountingExpenseAccountsForCompany &&
        <div className="table-responsive">
          <table className="table align-middle table-row-dashed gy-1 mb-2">
            <thead className="border-bottom">
              <tr className="text-muted text-uppercase fw-bolder fs-7">
                <th className="min-w-200px text-nowrap text-start">{lang.labels.description}</th>
                <th className="w-350px text-nowrap text-end">{lang.labels.debit}</th>
                <th className="w-350px text-nowrap text-end">{lang.labels.credit}</th>
              </tr>
            </thead>
            <tbody className="text-muted fw-bold">
              { contents.value.map (( (item, index) => { const gh = groupHeaders?.find(g => g.index === index); return (
                <React.Fragment key={index}>
                { gh &&
                  <tr className="bg-light">
                    <td colSpan={3} className="fw-bolder text-dark fs-7 py-2">{gh.label}</td>
                  </tr>
                }
                <tr>
                  <td className="text-secondary text-start">{item.content.detail}</td>
                  <td className="text-nowrap text-end">
                    { item.content.price_unit < 0 &&
                      <div className="d-flex flex-column">
                        <span className="form-select-sm">{accountingAccountSelected(accountingAccountsForCompany, item.debit.accounting_account, companyForUser.language)}</span>
                        <strong className="fs-8">{currencyCode} {formatMoney(toDisplay(item.debit.amount))}</strong>
                      </div>
                    }
                    { item.content.price_unit > 0 &&
                      <div className="d-flex flex-column">
                        <AppInputAccountingAccount component={component} options={{small: true, type: "debit", index: index, item: item}} companyForUser={companyForUser} accountingAccountsForCompany={accountingAccountsForCompany} accountingExpenseAccountsForCompany={buildDebitAccountList(accountingAccountsForCompany)} contentAccounts={contents} setContentAccounts={setContents}></AppInputAccountingAccount>
                        <strong className="fs-8">{currencyCode} {formatMoney(toDisplay(item.debit.amount))}</strong>
                      </div>
                    }
                    { !hideTaxAccounts && (settingsForCompany?.subject_tax === true) && (settingsForCompany?.tax_method ?? 'effective') === "effective" &&
                      <>
                        { ((settingsForCompany?.tax_declaration ?? 'invoicing') === "invoicing" || (settingsForCompany?.tax_declaration ?? 'invoicing') === "cashing") && taxPurchaseRecoverableCodes().includes(item.content.tax_code) &&
                          <>
                            <div className="d-flex flex-column">
                              <AppInputAccountingAccountTax companyForUser={companyForUser} accountingTaxAccountsForCompany={accountingTaxAccountsForCompany} indexContent={index} contentTaxAccounts={contentTaxAccounts} setContentTaxAccounts={setContentTaxAccounts}></AppInputAccountingAccountTax>
                              { (settingsForCompany?.tax_declaration ?? 'invoicing') === "invoicing" &&
                                <span className="fs-8">{currencyCode} {formatMoney(toDisplay(accumulatedEffectiveTaxDocuments(companyForUser, contents.value).deductible))}</span>
                              }
                              { (settingsForCompany?.tax_declaration ?? 'invoicing') === "cashing" &&
                                <span className="fs-8">{currencyCode} {formatMoney(0)}</span>
                              }
                            </div>
                            { (settingsForCompany?.tax_declaration ?? 'invoicing') === "cashing" &&
                              <div className="d-flex flex-column text-end text-nowrap">
                                <span className="fw-bold">{accountingAccountSelected(accountingAccountsForCompany, accountingAccountId(accountingAccountsForCompany, "1175"), companyForUser.language)}</span>
                                <span className="fs-8">{currencyCode} {formatMoney(toDisplay(accumulatedEffectiveTaxDocuments(companyForUser, contents.value).deductible))}</span>
                              </div>
                            }
                          </>
                        }
                        { ((settingsForCompany?.tax_declaration ?? 'invoicing') === "invoicing" || (settingsForCompany?.tax_declaration ?? 'invoicing') === "cashing") && taxPurchaseReverseChargeCodes().includes(item.content.tax_code) &&
                          <div className="d-flex flex-column">
                            <AppInputAccountingAccountTax companyForUser={companyForUser} accountingTaxAccountsForCompany={accountingTaxAccountsForCompany} indexContent={index} contentTaxAccounts={contentTaxAccounts} setContentTaxAccounts={setContentTaxAccounts}></AppInputAccountingAccountTax>
                            <span className="fs-8">{currencyCode} {formatMoney(toDisplay(accumulatedEffectiveTaxDocuments(companyForUser, contents.value).reverse_charge))}</span>
                          </div>
                        }
                      </>
                    }
                  </td>
                  <td className="text-nowrap text-end">
                    { item.content.price_unit < 0 &&
                      <div className="d-flex flex-column">
                        <AppInputAccountingAccount component={component} options={{small: true, type: "credit", index: index, item: item}} companyForUser={companyForUser} accountingAccountsForCompany={accountingAccountsForCompany} accountingExpenseAccountsForCompany={buildCreditNegativeAccountList(accountingAccountsForCompany)} contentAccounts={contents} setContentAccounts={setContents}></AppInputAccountingAccount>
                        <strong className="fs-8">{currencyCode} {formatMoney(toDisplay(item.credit.amount))}</strong>
                      </div>
                    }
                  </td>
                </tr>
                </React.Fragment>
              )}))}
            </tbody>
            <tfoot className="border-top text-secondary fw-bolder fs-7">
              <tr>
                <td>
                  <div className="d-flex flex-column text-start text-nowrap">
                    { creditParty
                      ? <>
                          <span className="fs-8">{creditParty.label}</span>
                          <span className="fw-bold">{creditParty.name}</span>
                        </>
                      : purchase === "expense"
                      ? <>
                          <span className="fs-8">{lang.labels.treasuryAccount}</span>
                          <span className="fw-bold">{record.treasury_account?.name || ''}</span>
                        </>
                      : <>
                          <span className="fs-8">{lang.labels.providerReceivable}</span>
                          <span className="fw-bold">{record.provider ? formatContact(record.provider) : '—'}</span>
                        </>
                    }
                  </div>
                </td>
                <td></td>
                <td>
                  <div className="d-flex flex-column text-end text-nowrap">
                    { creditParty
                      ? (() => {
                          const accId = accountingAccountId(accountingAccountsForCompany, creditParty.accountCode)
                          const display = accId
                            ? accountingAccountSelected(accountingAccountsForCompany, accId, companyForUser.language)
                            : null
                          return display
                            ? <span className="fw-bold">{display}</span>
                            : <span className="fw-bold text-danger">{creditParty.accountCode}</span>
                        })()
                      : purchase === "expense"
                      ? <>
                          { (() => {
                            const ta = treasuryAccountsForCompany?.find((t) => t.id === record.treasury_account?.id)
                            const accId = ta?.accounting_account?.id || ''
                            return <span className="fw-bold">{accountingAccountSelected(accountingAccountsForCompany, accId, companyForUser.language)}</span>
                          })() }
                        </>
                      : <span className="fw-bold">{accountingAccountSelected(accountingAccountsForCompany, accountingAccountId(accountingAccountsForCompany, "2000"), companyForUser.language)}</span>
                    }
                    <span className="fs-8">{currencyCode} {formatMoney(toDisplay(amounts.value.base))}</span>
                  </div>
                </td>
              </tr>
              { (settingsForCompany?.subject_tax === true) && amounts.value.tax_rc > 0 &&
                <tr>
                  <td>
                    <div className="d-flex flex-column text-start text-nowrap">
                      <span className="fs-8">{lang.labels.vatDue}</span>
                      <span className="fw-bold">
                        {purchase === "invoice" && record.provider_invoice_number}
                        {purchase === "expense" && record.expense_number}
                      </span>
                    </div>
                  </td>
                  <td></td>
                  <td>
                    <div className="d-flex flex-column text-end text-nowrap">
                      <span className="fw-bold">{accountingAccountSelected(accountingAccountsForCompany, accountingAccountId(accountingAccountsForCompany, "2203"), companyForUser.language)}</span>
                      <span className="fs-8">{currencyCode} {formatMoney(toDisplay(amounts.value.tax_rc))}</span>
                    </div>
                  </td>
                </tr>
              }
              <tr>
                <td className="text-start text-uppercase">{lang.labels.total}</td>
                <td className="text-end text-nowrap">{currencyCode} {formatMoney(toDisplay(amounts.value.debit))}</td>
                <td className="text-end text-nowrap">{currencyCode} {formatMoney(toDisplay(amounts.value.credit))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      }
    </div>
  )
};

export default AppPurchaseFormEntries;
